// This module defines metadata that is **version-agnostic**: the metadata are
// associated with *concepts* whose semantics should not change across versions.
//
// Why and how does this work? The BAT concept is identified by BAT name; the
// BAT field concept by BAT name plus field name. It is a key constraint of BAT
// schema management that those names must point to a semantically consistent
// concept (that is, consistent across versions): from one version to the next,
// we must never change the underlying *conceptual meaning* (to a human) of the
// BAT schema with a particular name, or the schema field with a particular
// name. (When we have a new concept to express, such that a "description" would
// no longer be consistent across versions, we express it with a new BAT or
// field, not by adjusting an existing one.)
//
// TODO: **Why do we have `quantity` fields below for BATs that don't really
// have a `quantity` field?** (Like, logistics.) The problem we have is that, as
// of 2023-08-04, the BART-EM matching process during evaluation renames any
// BART columns used as the EM's functional unit to `quantity`. For example, the
// logistics EM uses `weight_quantity` as the functional unit; during
// evaluation, that gets renamed to `quantity`. Then downstream, when we're
// trying to populate TraceDetails with metadata, we only know that the column
// was named `quantity` -- we don't know that it was *originally*
// `weight_quantity`. Obviously, we need to fix this situation: it is not only
// confusing, but also only works because of assumptions that we do not want to
// make about EMs (e.g. that every EM that uses a BAT will always use the same
// column as the functional unit).

import { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { BadDataError } from '@watershed/errors/BadDataError';
import { i18n } from '@watershed/intl';
import {
  assertNoTypeDifferences,
  noTypeDifferences,
} from '@watershed/shared-util/src/assertNoTypeDifferences';

import { captureException } from '../utils/sentry';
import { BatTypeNamesForTestingOnly, BusinessActivityTypeName } from './types';
import { buildingOccupationMetadata } from './batMetadata/buildingOccupation';
import { buildingStationaryCombustionMetadata } from './batMetadata/buildingStationaryCombustion';
import { buildingWasteMetadata } from './batMetadata/buildingWaste';
import { carbonCreditRetirementsMetadata } from './batMetadata/carbonCreditRetirements';
import { carbonRemovalMetadata } from './batMetadata/carbonRemoval';
import { cloudComputeUsageMetadata } from './batMetadata/cloudComputeUsage';
import { cloudSpendMetadata } from './batMetadata/cloudSpend';
import { corporate315InvestmentsMetadata } from './batMetadata/corporate315Investments';
import { dataTransferMetadata } from './batMetadata/dataTransfer';
import { directGhgEmissionsMetadata } from './batMetadata/directGhgEmissions';
import { electricityConsumptionMetadata } from './batMetadata/electricityConsumption';
import { electricityConsumptionDatacenterMetadata } from './batMetadata/electricityConsumptionDatacenter';
import { employeesMetadata } from './batMetadata/employees';
import { expenditureMetadata } from './batMetadata/expenditure';
import { expenditureMappedMetadata } from './batMetadata/expenditureMapped';
import { expenditureUnmappedMetadata } from './batMetadata/expenditureUnmapped';
import { financeCorporateAssetMetadata } from './batMetadata/financeCorporateAsset';
import { fuelCombustionMetadata } from './batMetadata/fuelCombustion';
import { lifetimeFuelAndElectricityUseOfSoldProductsMetadata } from './batMetadata/lifetimeFuelAndElectricityUseOfSoldProducts';
import { lodgingMetadata } from './batMetadata/lodging';
import { logisticsMetadata } from './batMetadata/logistics';
import { nonBuildingElectricityConsumptionMetadata } from './batMetadata/nonBuildingElectricityConsumption';
import { refrigerantsMetadata } from './batMetadata/refrigerants';
import { soldRefrigerantMetadata } from './batMetadata/soldRefrigerant';
import { soldRetailGoodMetadata } from './batMetadata/soldRetailGood';
import { soldVehicleMetadata } from './batMetadata/soldVehicle';
import { travelAirMetadata } from './batMetadata/travelAir';
import { travelPrivateJetsMetadata } from './batMetadata/travelPrivateJets';
import { travelRailMetadata } from './batMetadata/travelRail';
import { vehiclesMetadata } from './batMetadata/vehicles';
import { wasteMetadata } from './batMetadata/waste';
import { wasteProductsMetadata } from './batMetadata/wasteProducts';
import { activityWithCustomEfMetadata } from './batMetadata/activityWithCustomEf';
import { antiBriberyAndCorruptionIncidentsMetadata } from './batMetadata/antiBriberyAndCorruptionIncidents';
import { cloudUsageAndSpendMetadata } from './batMetadata/cloudUsageAndSpend';
import { cryptoHoldingsMetadata } from './batMetadata/cryptoHoldings';
import { cryptoTransactionsMetadata } from './batMetadata/cryptoTransactions';
import { foodMassMetadata } from './batMetadata/foodMass';
import { foodSpendMetadata } from './batMetadata/foodSpend';
import { healthAndSafetyIncidentsMetadata } from './batMetadata/healthAndSafetyIncidents';
import { purchaseOrdersMetadata } from './batMetadata/purchaseOrders';
import { substanceInflowMetadata } from './batMetadata/substanceInflow';
import { substanceOutflowMetadata } from './batMetadata/substanceOutflow';
import { tier1FactoriesActivityDataMetadata } from './batMetadata/tier1FactoriesActivityData';
import { tier1FactoriesSpendMetadata } from './batMetadata/tier1FactoriesSpend';
import { waterConsumptionMetadata } from './batMetadata/waterConsumption';
import { waterDischargeMetadata } from './batMetadata/waterDischarge';
import { waterRecyclingMetadata } from './batMetadata/waterRecycling';
import { waterStorageMetadata } from './batMetadata/waterStorage';
import { waterUsageMetadata } from './batMetadata/waterUsage';
import { workplaceReportsMetadata } from './batMetadata/workplaceReports';
import { workersSocialMetadata } from './batMetadata/workersSocial';
import { ghgRemovalMetadata } from './batMetadata/ghgRemoval';
import { microplasticsOutflowMetadata } from './batMetadata/microplasticsOutflow';
import { lobbyingMetadata } from './batMetadata/lobbying';
import { historicalEmissionsMetadata } from './batMetadata/historicalEmissions';
import { intensityDenominatorMonetaryMetadata } from './batMetadata/intensityDenominatorMonetary';
import { intensityDenominatorCustomMetadata } from './batMetadata/intensityDenominatorCustom';
import { consumerGoodsEcoinventMaterialMetadata } from './batMetadata/consumerGoodsEcoinventMaterial';
import { consumerGoodsMaterialAssessmentMetadata } from './batMetadata/consumerGoodsMaterialAssessment';
import { consumerGoodsPackagingMaterialMetadata } from './batMetadata/consumerGoodsPackagingMaterial';
import { consumerGoodsTextileProductUsePhaseMetadata } from './batMetadata/consumerGoodsTextileProductUsePhase';
import { consumerGoodsTextileProductManufacturingMetadata } from './batMetadata/consumerGoodsTextileProductManufacturing';
import { consumerGoodsProductAssessmentMetadata } from './batMetadata/consumerGoodsProductAssessment';
import { ceeBuildingMetadata } from './batMetadata/ceeBuilding';
import { ceeBuildingScope1Metadata } from './batMetadata/ceeBuildingScope1';
import { ceeBuildingScope2Metadata } from './batMetadata/ceeBuildingScope2';
import { ceeBuildingScope3Metadata } from './batMetadata/ceeBuildingScope3';
import { ceeCloudSpendMetadata } from './batMetadata/ceeCloudSpend';
import { ceeCommutingMetadata } from './batMetadata/ceeCommuting';
import { ceeEeioScope1Metadata } from './batMetadata/ceeEeioScope1';
import { ceeElectricityUsageMetadata } from './batMetadata/ceeElectricityUsage';
import { ceeElectricityUsageScope2Metadata } from './batMetadata/ceeElectricityUsageScope2';
import { ceeElectricityUsageScope33TdLossMetadata } from './batMetadata/ceeElectricityUsageScope33TdLoss';
import { ceeElectricityUsageScope33WttMetadata } from './batMetadata/ceeElectricityUsageScope33Wtt';
import { ceeElectricityUsageScope33WttTdLossMetadata } from './batMetadata/ceeElectricityUsageScope33WttTdLoss';
import { ceeFlightsMetadata } from './batMetadata/ceeFlights';
import { ceeHomeOfficeMetadata } from './batMetadata/ceeHomeOffice';
import { ceeNaicsSpendMetadata } from './batMetadata/ceeNaicsSpend';
import { ceePrecalculatedMetadata } from './batMetadata/ceePrecalculated';
import { ceeVehiclesMetadata } from './batMetadata/ceeVehicles';
import { siteLandUseMetadata } from './batMetadata/siteLandUse';
import { soldProductCircularityMetadata } from './batMetadata/soldProductCircularity';
import { soldProductRecyclabilityMetadata } from './batMetadata/soldProductRecyclability';
import { severeHumanRightsIncidentsMetadata } from './batMetadata/severeHumanRightsIncidents';
import { paymentOrdersMetadata } from './batMetadata/paymentOrders';
import type {
  FieldMetadataMessageDescriptorDef,
  BatMetadataConfigEntry,
} from './batMetadata/common/types';
export type { FieldMetadataMessageDescriptorDef, BatMetadataConfigEntry };

// This should never be displayed to users itself!
export const GLOBAL_KEY = '__GLOBAL__';

const standardDistanceQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Distance',
    context: 'The distance traveled.',
  }),
  externalDescription: msg({
    message: 'The distance traveled.',
    context: 'Distance',
  }),
};

const currencyQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Currency quantity',
    context: 'The expenditure amount.',
  }),
  externalDescription: msg({
    message: 'The expenditure amount.',
    context: 'Currency quantity',
  }),
};

const locationCity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'City',
    context: 'The city in which the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The city in which the business activity took place.',
    context: 'City',
  }),
};

const locationCountry: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Country',
    context: 'The country in which the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The country in which the business activity took place.',
    context: 'Country',
  }),
};

const month: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Month',
    context: 'The month the business activity took place.',
  }),
  externalDescription: msg({
    message: 'The month the business activity took place.',
    context: 'Month',
  }),
};

const distanceUnit: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Distance unit',
    context: 'The unit of distance traveled.',
  }),
  externalDescription: msg({
    message: 'The unit of distance traveled.',
    context: 'Distance unit',
  }),
};

/**
 * I18n message descriptors for BATs and their fields
 */
export const BAT_DISPLAY_CONFIG = {
  [GLOBAL_KEY]: {
    // eslint-disable-next-line @watershed/l10n-string-context, lingui/no-single-variables-to-translate
    displayName: msg({ message: `${GLOBAL_KEY}` }), // this should never be displayed to users itself!
    fields: {
      clean_power_percent: {
        displayName: msg({
          message: 'Clean power percent',
          context: 'The clean power percent of the facility.',
        }),
        externalDescription: msg({
          message: 'The clean power percent of the facility.',
          context: 'Clean power percent',
        }),
      },
      currency_quantity: currencyQuantity,
      distance_quantity: standardDistanceQuantity,
      distance_unit: distanceUnit,
      onsite_clean_power_percent: {
        displayName: msg({
          message: 'Onsite clean power percent',
          context:
            'The percentage of energy generated from renewable sources onsite',
        }),
        externalDescription: msg({
          message:
            'The percentage of energy generated from renewable sources onsite',
          context: 'Onsite clean power percent',
        }),
      },
      passenger_quantity: {
        displayName: msg({
          message: 'Passenger quantity',
          context: 'The number of passengers.',
        }),
        externalDescription: msg({
          message: 'The number of passengers.',
          context: 'Passenger quantity',
        }),
      },
      location_city: locationCity,
      location_country: locationCountry,
      month,
    },
  },

  employees: employeesMetadata,

  // Used for TSchema migration by Data Infra
  dummy_employees: {
    displayName: msg({
      message: 'Dummy employees',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  building_stationary_combustion: buildingStationaryCombustionMetadata,
  building_waste: buildingWasteMetadata,
  cooling: {
    displayName: msg({
      message: 'Cooling',
      context: 'Activity data schema name',
    }),
    fields: {},
  },
  cloud_compute_usage: cloudComputeUsageMetadata,
  cloud_spend: cloudSpendMetadata,
  cloud_usage_and_spend: cloudUsageAndSpendMetadata,
  cee_building: ceeBuildingMetadata,
  cee_building_scope_1: ceeBuildingScope1Metadata,
  cee_building_scope_2: ceeBuildingScope2Metadata,
  cee_building_scope_3: ceeBuildingScope3Metadata,
  cee_cloud_spend: ceeCloudSpendMetadata,
  cee_commuting: ceeCommutingMetadata,
  cee_eeio_scope_1: ceeEeioScope1Metadata,
  cee_electricity_usage: ceeElectricityUsageMetadata,
  cee_electricity_usage_scope_2: ceeElectricityUsageScope2Metadata,
  cee_electricity_usage_scope_3_3_td_loss:
    ceeElectricityUsageScope33TdLossMetadata,
  cee_electricity_usage_scope_3_3_wtt: ceeElectricityUsageScope33WttMetadata,
  cee_electricity_usage_scope_3_3_wtt_td_loss:
    ceeElectricityUsageScope33WttTdLossMetadata,
  cee_flights: ceeFlightsMetadata,
  cee_home_office: ceeHomeOfficeMetadata,
  cee_naics_spend: ceeNaicsSpendMetadata,
  cee_precalculated: ceePrecalculatedMetadata,
  cee_vehicles: ceeVehiclesMetadata,
  consumer_goods_ecoinvent_material: consumerGoodsEcoinventMaterialMetadata,
  consumer_goods_material_assessment: consumerGoodsMaterialAssessmentMetadata,
  consumer_goods_packaging_material: consumerGoodsPackagingMaterialMetadata,
  consumer_goods_textile_product_use_phase:
    consumerGoodsTextileProductUsePhaseMetadata,
  corporate_315_investments: corporate315InvestmentsMetadata,
  direct_ghg_emissions: directGhgEmissionsMetadata,
  data_transfer: dataTransferMetadata,
  electricity_consumption: electricityConsumptionMetadata,
  expenditure: expenditureMetadata,
  expenditure_mapped: expenditureMappedMetadata,
  finance_corporate_asset: financeCorporateAssetMetadata,
  non_building_electricity_consumption:
    nonBuildingElectricityConsumptionMetadata,
  electricity_consumption_datacenter: electricityConsumptionDatacenterMetadata,
  food_mass: foodMassMetadata,
  food_spend: foodSpendMetadata,
  fuel_combustion: fuelCombustionMetadata,
  refrigerants: refrigerantsMetadata,
  sold_retail_good: soldRetailGoodMetadata,
  building_occupation: buildingOccupationMetadata,
  logistics: logisticsMetadata,
  lifetime_fuel_and_electricity_use_of_sold_products:
    lifetimeFuelAndElectricityUseOfSoldProductsMetadata,
  sold_refrigerant: soldRefrigerantMetadata,
  sold_vehicle: soldVehicleMetadata,
  lodging: lodgingMetadata,
  waste: wasteMetadata,
  waste_products: wasteProductsMetadata,
  water_usage: waterUsageMetadata,
  carbon_removal: carbonRemovalMetadata,
  ghg_removal: ghgRemovalMetadata,
  carbon_credit_retirements: carbonCreditRetirementsMetadata,
  vehicles: vehiclesMetadata,
  tier_1_factories_activity_data: tier1FactoriesActivityDataMetadata,
  tier_1_factories_spend: tier1FactoriesSpendMetadata,
  travel_air: travelAirMetadata,
  travel_private_jets: travelPrivateJetsMetadata,
  travel_rail: travelRailMetadata,
  historical_emissions: historicalEmissionsMetadata,

  historical_emissions_pv2: {
    displayName: msg({
      message: 'Historical emissions from pipeline v2',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  water_consumption: waterConsumptionMetadata,
  water_recycling: waterRecyclingMetadata,
  water_storage: waterStorageMetadata,
  water_discharge: waterDischargeMetadata,
  expenditure_unmapped: expenditureUnmappedMetadata,

  physical_goods_purchase: {
    displayName: msg({
      message: 'Physical goods purchase',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  purchase_orders: purchaseOrdersMetadata,
  crypto_holdings: cryptoHoldingsMetadata,
  crypto_transactions: cryptoTransactionsMetadata,
  consumer_goods_product_assessment: consumerGoodsProductAssessmentMetadata,
  consumer_goods_textile_product_manufacturing:
    consumerGoodsTextileProductManufacturingMetadata,
  corporate_asset_holding: {
    displayName: msg({
      message: 'Corporate asset holdings',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  real_estate_asset_holding: {
    displayName: msg({
      message: 'Real Estate asset holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  asset_group_holding: {
    displayName: msg({
      message: 'Asset Group holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly outstanding amount held',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly outstanding amounts.',
          context: 'Total monthly outstanding amount held',
        }),
      },
    },
  },

  personal_motor_vehicle_insurance_holding: {
    displayName: msg({
      message: 'Personal motor vehicle insurance holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Number of holdings, always 1.',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'Each holding represents 1 personal motor vehicle insurance policy.',
          context: 'Holding for a personal motor vehicle insurance policy.',
        }),
      },
    },
  },

  sovereign_debt_holding: {
    displayName: msg({
      message: 'Sovereign debt holding',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Total monthly exposure amount',
          context: 'The quantity of the asset.',
        }),
        externalDescription: msg({
          message:
            'If the time period contains multiple months, this value sums the monthly exposure amounts.',
          context:
            'Description of a field of data describing amounts of money.',
        }),
      },
    },
  },

  insurance_commercial_line_holding: {
    displayName: msg({
      message: 'Insurance commercial line holding',
      context:
        'Activity data schema name; represents a commercial line financial holding in a corporate entity.',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Number of holdings',
          context: 'The number of holdings this field represents.',
        }),
        externalDescription: msg({
          message:
            'Each holding represents the premium for a commercial lines holding.',
          context:
            'Description of a numeric field of data that shows the premium for a commercial line financial holding.',
        }),
      },
    },
  },

  legacy_finance_emissions: {
    displayName: msg({
      message: 'Legacy finance emissions',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  substances: {
    displayName: msg({
      message: 'Substances',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  substance_outflow: substanceOutflowMetadata,
  substance_inflow: substanceInflowMetadata,
  microplastics_outflow: microplasticsOutflowMetadata,
  site_land_use: siteLandUseMetadata,
  intensity_denominator_monetary: intensityDenominatorMonetaryMetadata,
  intensity_denominator_custom: intensityDenominatorCustomMetadata,

  custom: {
    displayName: msg({
      message: 'Custom',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  legacy_clean_power: {
    displayName: msg({
      message: 'Clean power',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  cadt: {
    displayName: msg({
      message: 'Clean activity data table',
      context: 'Activity data schema name',
    }),
    fields: {},
  },

  em_development_bat: {
    displayName: msg({
      message: 'EM development',
      context: 'Activity data schema name',
    }),
    fields: {
      quantity: {
        displayName: msg({
          message: 'Quantity',
          context: 'The quantity of the emissive activity.',
        }),
        externalDescription: msg({
          message: 'The quantity of the emissive activity.',
          context: 'Quantity',
        }),
      },
    },
  },

  activity_with_custom_ef: activityWithCustomEfMetadata,
  sold_product_circularity: soldProductCircularityMetadata,
  sold_product_recyclability: soldProductRecyclabilityMetadata,
  workplace_reports: workplaceReportsMetadata,
  health_and_safety_incidents: healthAndSafetyIncidentsMetadata,
  severe_human_rights_incidents: severeHumanRightsIncidentsMetadata,
  anti_bribery_and_corruption_incidents:
    antiBriberyAndCorruptionIncidentsMetadata,
  payment_orders: paymentOrdersMetadata,
  lobbying: lobbyingMetadata,
  workers_social: workersSocialMetadata,
} as const satisfies Record<string, BatMetadataConfigEntry>;

// -----> Below is the interface implementation. We should not need to change
// this just because we're updating definition.

// Display names are required but descriptions are optional so that we can add
// descriptions over time without breaking changes.

type FieldMetadataDef = {
  displayName: string;
  externalDescription?: string;
};

type BatMetadataDef = {
  /**
   * All fields, keyed by field name.
   */
  fields: Map<string, FieldMetadataMessageDescriptorDef>;

  /**
   * A human-readable display name for this BAT.
   */
  displayName: MessageDescriptor;
};

export type MetadataDefinedPrimaryTypes = Exclude<
  keyof typeof BAT_DISPLAY_CONFIG,
  typeof GLOBAL_KEY
>;
// Assert that all BATs in the current schema are covered by the metadata
// We don't assert that they are identical, since we want metadata coverage
// to live on even after a BAT is removed from †he current version.
type UncoveredCurrentBATs = Exclude<
  Exclude<BusinessActivityTypeName, MetadataDefinedPrimaryTypes>,
  // Why is this here? We have a list of test only types in the types file,
  // that were added to write integration tests. We should clean these up
  // but for now we will just ensure we can skip them in this customer facing metadata
  BatTypeNamesForTestingOnly
>;
assertNoTypeDifferences<UncoveredCurrentBATs, never>(noTypeDifferences);

// Keyed by BAT name.
const batMetadataLookup = new Map<string, BatMetadataDef>(
  Object.entries(BAT_DISPLAY_CONFIG).map(([batName, batDef]) => {
    return [
      batName,
      {
        fields: new Map(Object.entries(batDef.fields)),
        displayName: batDef.displayName,
      },
    ];
  })
);

export type BatDisplayConfig = typeof BAT_DISPLAY_CONFIG;
export type BatSchemaName = keyof BatDisplayConfig;

/**
 * BatMetadata exposes functions for accessing BAT metadata definitions.
 */
export class BatMetadata {
  static getFieldMetadataMessageDescriptors<
    B extends BatSchemaName,
    F extends keyof BatDisplayConfig[B]['fields'],
  >(batName: B, fieldName: F): FieldMetadataMessageDescriptorDef {
    const batSchemaMetadata = BAT_DISPLAY_CONFIG[batName];
    return batSchemaMetadata?.fields[
      fieldName as keyof BatDisplayConfig[BatSchemaName]['fields']
    ];
  }

  /**
   * Returns the field metadata for a particular BAT field, or undefined if
   * there is no metadata defined for that field.
   */
  static getFieldMetadata(
    batName: string,
    fieldName: string,
    scopedI18n: I18n = i18n
  ): FieldMetadataDef | undefined {
    const fieldMetadata: FieldMetadataMessageDescriptorDef | undefined =
      BatMetadata.getFieldMetadataMessageDescriptors(
        batName as BatSchemaName,
        fieldName as keyof BatDisplayConfig[BatSchemaName]['fields']
      );
    if (!fieldMetadata) {
      return;
    }
    return {
      displayName: scopedI18n._(fieldMetadata.displayName),
      externalDescription: fieldMetadata.externalDescription
        ? scopedI18n._(fieldMetadata.externalDescription)
        : undefined,
    };
  }

  /**
   * Our BAT names are slugs like `building_stationary_combustion`. This
   * returns a human-readable display name, like "Building stationary combustion".
   * @deprecated Use BusinessActivityTypeService.getBatDisplayName instead. We now
   * have custom activity types that can't have static names.
   */
  static getDisplayName(batName: string, scopedI18n: I18n = i18n): string {
    BadDataError.invariant(
      batName !== GLOBAL_KEY,
      'Cannot get display name for global BAT name key'
    );
    const mappedName = batMetadataLookup.get(batName)?.displayName;
    if (!mappedName) {
      captureException(
        new Error(
          `[soft invariant] Could not fetch display name for BAT ${batName}. ` +
            'Please confirm the bat name is covered in BatMetadata.ts. Note that every BAT should ' +
            'remain covered in the metadata even if the BAT is removed from the current version.'
        ),
        { level: 'warning' }
      );
    }
    return mappedName ? scopedI18n._(mappedName) : batName;
  }

  /**
   * Returns the display name for a BAT as a MessageDescriptor, or undefined
   * if not found.
   * @deprecated Use BusinessActivityTypeService.getBatDisplayName instead. We now
   * have custom activity types that can't have static names.
   */
  static getDisplayNameMessageDescriptor(
    batName: string
  ): MessageDescriptor | undefined {
    return batMetadataLookup.get(batName)?.displayName ?? undefined;
  }

  /**
   * Applies display name overrides for activity types that need disambiguation.
   * Currently only applies to expenditure types to distinguish legacy vs standard.
   * Returns the full display name to use.
   *
   * This is a temporary override we're applying only in flexible footprint until
   * we're able to make the switch more broadly in the UI and won't need the override.
   */
  static applyDisplayNameOverride(
    displayName: string,
    batName: string,
    i18n: I18n
  ): string {
    if (batName === 'expenditure') {
      return i18n._(
        msg({
          message: 'Expenses (legacy)',
          context: 'Display name for legacy expenditure activity type',
        })
      );
    }
    return displayName;
  }
}
